import React from "react";
import { useState, useEffect, useRef } from "react";
import {
  Row,
  Col,
  Image,
  Container,
  ListGroup,
  Button,
  Form,
  Alert,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import Meta from "../components/Meta";
import {
  useSubmitRestorationMutation,
  // useUploadRestorationSubmissionFileMutation, // file upload commented out - uncomment to revert
} from "../slices/submissionApiSlice";
import Loader from "../components/Loader";
import { validateForm, formatPhoneNumber } from "../utils/formValidation";
import RecaptchaCheckbox from "../components/RecaptchaCheckbox";

const RestorationScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [brandModel, setBrandModel] = useState("");
  const [description, setDescription] = useState("");
  // const [file, setFile] = useState(null); // file upload commented out for easy revert
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const recaptchaRef = useRef(null);

  const [submitRestoration, { isLoading: isSubmitting }] =
    useSubmitRestorationMutation();
  // const [uploadSubmissionFile, { isLoading: isUploading }] =
  //   useUploadRestorationSubmissionFileMutation(); // file upload commented out for easy revert

  useEffect(() => {
    if (location.hash !== "#inquiry") return;
    const id = setTimeout(() => {
      const el = document.getElementById("inquiry");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(id);
  }, [location.pathname, location.hash]);

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    // Skip validation for email field - let browser handle it
    if (field !== "email") {
      validateField(field);
    }
  };

  const validateField = (field) => {
    const formData = { name, email, phone, subject, brandModel, description };
    const validation = validateForm(formData, "restoration");
    setErrors((prev) => ({
      ...prev,
      [field]: validation.errors[field] || null,
    }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setPhone(value);
    if (touched.phone) {
      validateField("phone");
    }
  };

  const handlePhoneBlur = () => {
    setTouched({ ...touched, phone: true });
    // Format phone number on blur
    const formatted = formatPhoneNumber(phone);
    setPhone(formatted);
    validateField("phone");
  };

  /* file upload commented out for easy revert
  const uploadFileHandler = async (e) => {
    const fileData = new FormData();
    fileData.append("file", e.target.files[0]);
    fileData.append("formType", "restoration");
    try {
      const uploadResponse = await uploadSubmissionFile(fileData).unwrap();
      toast.success("File uploaded successfully!");
      setFile(uploadResponse.file);
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };
  */

  const submitHandler = async (e) => {
    e.preventDefault();

    const formData = {
      name,
      email,
      phone: phone.replace(/\D/g, ""), // Store only digits
      subject,
      brandModel,
      description,
      file: null, // file upload commented out; was: file
    };

    // Validate entire form
    const validation = validateForm(formData, "restoration");

    if (!validation.isValid) {
      setErrors(validation.errors);
      setTouched({
        name: true,
        email: true,
        phone: true,
        subject: true,
        brandModel: true,
        description: true,
      });
      toast.error("Please correct the errors in the form");
      return;
    }

    if (!recaptchaToken) {
      toast.error("Please complete the reCAPTCHA checkbox");
      return;
    }

    try {
      await submitRestoration({ ...formData, recaptchaToken }).unwrap();
      recaptchaRef.current?.reset();
      setRecaptchaToken("");
      toast.success("Submitting form...");
      navigate("/restoration");
      toast.success("Form submitted successfully!");
      // Clear the form
      setName("");
      setEmail("");
      setPhone("");
      setSubject("");
      setBrandModel("");
      setDescription("");
      // setFile(null); // file upload commented out for easy revert
      setErrors({});
      setTouched({});
    } catch (err) {
      recaptchaRef.current?.reset();
      setRecaptchaToken("");
      toast.error(err?.data?.message || err.error);
    }
  };

  return (
    <>
      <Meta
        title="Restoration Services | Lugo Parts & Restoration"
        description="Discover our specialized restoration services for classic GMC trucks. From minor touch-ups to full-scale restorations, we bring your vintage GMC trucks back to life with unmatched quality and craftsmanship."
        keywords="Restoration Services, Classic GMC Trucks, Specialized, Minor Touch-Ups, Full-Scale, Vintage, Quality, Craftsmanship, Expertise, Professional, Authentic, Customization, Rebuild, Revive, Originality, Preservation, Refurbishment"
      />

      <Container>
        <Alert
          variant="info"
          className="mt-4 mb-4"
          style={{ borderLeft: "4px solid #d80607" }}
        >
          <Alert.Heading style={{ fontSize: "1.1rem", marginBottom: "0.5rem" }}>
            Ready to start your restoration project?
          </Alert.Heading>
          <p className="mb-0" style={{ fontSize: "0.95rem" }}>
            If you'd like to submit an inquiry about your restoration project,
            you can find our submission form at the bottom of this page.
          </p>
        </Alert>
        {/* Restoration 1 */}
        <section className="about-me mt-5 mb-5">
          <h3 className="mb-5">Comprehensive Restoration Services</h3>
          <Row className="align-items-center">
            <Col md={6}>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <p className="landing-page-text">
                    Our commitment to excellence is evident in our full
                    restoration service, which entails a meticulous frame-off
                    restoration process. No detail goes overlooked as we
                    meticulously dismantle, inspect, and restore each component,
                    ensuring that every nut and bolt receives the care and
                    attention it deserves.
                  </p>
                </ListGroup.Item>
              </ListGroup>
            </Col>
            <Col md={6}>
              <Image
                src="/images/compressed/webp/Restoration01.webp"
                fluid
                rounded
              />
            </Col>
          </Row>
        </section>

        {/* Featured Restoration Section */}
        <section className="featured-restoration mt-5 mb-5">
          <h3 className="mb-5">Featured Restoration Projects</h3>
          <Row className="align-items-center">
            <Col md={6}>
              <Image
                src="/images/compressed/webp/Restoration02.webp"
                fluid
                rounded
              />
            </Col>
            <Col md={6}>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <p className="landing-page-text">
                    The result is a true automotive masterpiece that seamlessly
                    marries classic design with modern precision. Whether you're
                    seeking to rekindle the glory of a bygone era or are in
                    pursuit of the perfect finishing touch for your vintage
                    vehicle, Lugo Parts is your trusted partner in breathing new
                    life into automotive legends.
                    <br />
                    <br />
                    Join us on this journey to relive history through
                    meticulously restored classic vehicles.
                  </p>
                </ListGroup.Item>
              </ListGroup>
            </Col>
          </Row>
        </section>

        {/* Restoration Process */}
        <section className="restoration-process mt-5 mb-5">
          <h3 className="mb-5">Our Restoration Process</h3>
          <Row className="align-items-center">
            <Col md={6}>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <p className="landing-page-text">
                    Our restoration process is a labor of love that starts with
                    a detailed assessment and ends with a masterpiece. From
                    engine rebuilds to upholstery, we handle every aspect with
                    precision and care.
                    <br />
                    <br />
                    Our multi-step process ensures that each vehicle is restored
                    to its former glory, capturing the essence of its original
                    design.
                  </p>
                </ListGroup.Item>
              </ListGroup>
            </Col>
            <Col md={6}>
              <Image
                src="/images/compressed/webp/Restoration03.webp"
                fluid
                rounded
              />
            </Col>
          </Row>
        </section>

        {/* Your Journey Begins Here */}
        <section className="your-journey-begins mt-5 mb-5">
          <h3 className="mb-5">Your Journey Begins Here</h3>
          <Row className="align-items-center">
            <Col md={6}>
              <Image
                src="/images/compressed/webp/Restoration05.webp"
                fluid
                rounded
              />
            </Col>
            <Col md={6}>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <p className="landing-page-text">
                    The road to a fully restored classic vehicle is a journey
                    filled with excitement, nostalgia, and endless
                    possibilities. At Lugo Parts & Restoration, we're not just
                    restoring vehicles; we're restoring dreams.
                    <br />
                    <br />
                    As you prepare to take the next step, know that we're here
                    to guide you through every twist and turn. Your journey
                    begins here, and we can't wait to see where it takes us.
                  </p>
                </ListGroup.Item>
              </ListGroup>
            </Col>
          </Row>
        </section>

        {/* Custom Restoration Inquiry Section */}
        <section id="inquiry" className="custom-inquiry mt-5 mb-5">
          <h3 className="mb-5" style={{ textAlign: "center" }}>
            Inquire About Your Restoration Project
          </h3>
          <Row className="align-items-center">
            <Col md={12}>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <p className="landing-page-text">
                    Dreaming of hitting the open road in a fully restored
                    1955-1959 Chevrolet or GMC truck? You're at the right place!
                    At Lugo Parts, we specialize in bringing these vintage
                    beauties back to life, and we'd love to hear about your
                    dream project.
                    <br />
                    <br />
                  </p>
                  <p className="landing-page-text">
                    Fill out the form below with your specific needs and let's
                    make your dream truck a reality. Our team of experts will
                    review your inquiry and get back to you with tailored
                    options to meet your unique requirements.
                  </p>
                </ListGroup.Item>
              </ListGroup>
            </Col>
          </Row>
          <Form onSubmit={submitHandler}>
            <Form.Group controlId="name" className="my-3">
              <Form.Label>Your Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => handleBlur("name")}
                className={errors.name && touched.name ? "is-invalid" : ""}
                required
              />
              {errors.name && touched.name && (
                <div className="form-error">{errors.name}</div>
              )}
            </Form.Group>

            <Form.Group controlId="email" className="my-3">
              <Form.Label>Email Address *</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => handleBlur("email")}
                required
              />
            </Form.Group>

            <Form.Group controlId="phone" className="my-3">
              <Form.Label>Phone Number *</Form.Label>
              <Form.Control
                type="tel"
                placeholder="Enter your phone number (with area code)"
                value={phone}
                onChange={handlePhoneChange}
                onBlur={handlePhoneBlur}
                className={errors.phone && touched.phone ? "is-invalid" : ""}
                required
              />
              {errors.phone && touched.phone && (
                <div className="form-error">{errors.phone}</div>
              )}
              <Form.Text className="text-muted">
                Include area code (e.g., 555-123-4567)
              </Form.Text>
            </Form.Group>

            <Form.Group controlId="subject" className="my-3">
              <Form.Label>Subject *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter the subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                onBlur={() => handleBlur("subject")}
                className={
                  errors.subject && touched.subject ? "is-invalid" : ""
                }
                required
              />
              {errors.subject && touched.subject && (
                <div className="form-error">{errors.subject}</div>
              )}
            </Form.Group>

            <Form.Group controlId="brandModel" className="my-3">
              <Form.Label>Brand/Model *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Brand or Model (or both)"
                value={brandModel}
                onChange={(e) => setBrandModel(e.target.value)}
                onBlur={() => handleBlur("brandModel")}
                className={
                  errors.brandModel && touched.brandModel ? "is-invalid" : ""
                }
                required
              />
              {errors.brandModel && touched.brandModel && (
                <div className="form-error">{errors.brandModel}</div>
              )}
            </Form.Group>

            <Form.Group controlId="description" className="my-3">
              <Form.Label>Restoration Goals *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Describe your restoration needs..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={() => handleBlur("description")}
                className={
                  errors.description && touched.description ? "is-invalid" : ""
                }
                required
              />
              {errors.description && touched.description && (
                <div className="form-error">{errors.description}</div>
              )}
            </Form.Group>

            {/* file upload commented out for easy revert
            <Form.Group controlId="file" className="my-3">
              <Form.Label>File Upload (PDF)</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf"
                onChange={uploadFileHandler}
              />
            </Form.Group>
            */}

            <div
              className="my-3 p-3 rounded"
              style={{
                backgroundColor: "#f8f9fa",
                borderLeft: "4px solid #d80607",
                color: "#1a1a1a",
              }}
            >
              <strong style={{ fontSize: "1rem", color: "#1a1a1a" }}>
                Sending vehicle photos
              </strong>
              <p
                className="mb-0 mt-2"
                style={{
                  fontSize: "1rem",
                  lineHeight: 1.5,
                  color: "#1a1a1a",
                }}
              >
                Please email{" "}
                <a
                  href="mailto:lugoparts@yahoo.com"
                  style={{
                    color: "#0d6efd",
                    fontWeight: 600,
                    textDecoration: "underline",
                  }}
                >
                  lugoparts@yahoo.com
                </a>{" "}
                or text{" "}
                <a
                  href="tel:916-652-0840"
                  style={{
                    color: "#0d6efd",
                    fontWeight: 600,
                    textDecoration: "underline",
                  }}
                >
                  916-652-0840
                </a>{" "}
                with 5–10 pictures of the vehicle along with your first and last
                name.
              </p>
            </div>

            <RecaptchaCheckbox
              ref={recaptchaRef}
              action="restoration_submit"
              onTokenChange={setRecaptchaToken}
            />

            <Button
              type="submit"
              variant="primary"
              className="mt-2"
              disabled={isSubmitting}
            >
              Submit Inquiry
            </Button>
            {isSubmitting && <Loader />}
          </Form>
        </section>
      </Container>
    </>
  );
};

export default RestorationScreen;
